import { colors, Divider, Tab, Tabs } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { AuthGuard, ErrorMessage, GenericHeader, LoadingIndicator, Page, RenderGuard, BookingList } from "components";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from "react-router-dom";
import { Slot } from "services";
import { ModelsActions } from "store/actions";
import { curriculum_manager } from 'utils/constants';
import useErrorCatcher from "utils/useErrorCatcher";
import Summary from "./Summary";
import useRouter from "utils/useRouter";

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3)
  },
  tabs: {
    marginTop: theme.spacing(3)
  },
  divider: {
    backgroundColor: colors.grey[300]
  },
  content: {
    marginTop: theme.spacing(3)
  }
}));

let mounted = false;

const SlotDetail = props => {
  const { match, history } = props;
  const classes = useStyles();
  const { slot_id, account_id, tab } = match.params;
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const runAsyncBlock = useErrorCatcher(error => setErrorMessage(error && error.message));
  const model = useSelector(state => state.models.slot);
  const dispatch = useDispatch();
  const router = useRouter();

  useEffect(() => {
    reload();
    // eslint-disable-next-line
  }, [slot_id]);

  useEffect(() => {
    mounted = true;
    return () => {
      mounted = false;
    }
  }, []);

  const reload = () => {
    setLoading(true);
    runAsyncBlock(async () => {
      const { model } = await Slot.detail(slot_id);
      dispatch(ModelsActions.update_model({ slot: model }));
    }).finally(() => setLoading(false));
  }

  const handleTabsChange = (event, value) => {
    history.push(value);
  };

  const tabs = [
    { value: "summary", label: "Summary" },
    { value: "bookings", label: "Bookings" },
  ];

  if (!tab) {
    return <Redirect to={`/accounts/${account_id}/slot/${slot_id}/summary`} />;
  }

  if (!tabs.find(t => t.value === tab)) {
    return <Redirect to="/errors/error-404" />;
  }

  const handleClose = () => {
    setLoading(true);
    runAsyncBlock(async () => {
      await Slot.close(slot_id);
      router.history.push('/overview');
    }).finally(() => mounted && setLoading(false));
  }

  const handleDelete = () => {
    setLoading(true);
    runAsyncBlock(async () => {
      await Slot.del(slot_id);
      router.history.push('/accounts');
    }).finally(() => mounted && setLoading(false));
  }

  return (
    <AuthGuard roles={[curriculum_manager]} projects={[]}>
      <Page className={classes.root} title="Slot Details" >
        <ErrorMessage message={errorMessage} />
        <LoadingIndicator variant="card" active={loading} />
        <RenderGuard renderIf={!loading && model}>
          {model && <GenericHeader subheader={model.name_en} header="Slot" subtitle={model.reference} />}
          <Tabs
            className={classes.tabs}
            onChange={handleTabsChange}
            scrollButtons="auto"
            value={tab}
            variant="scrollable" >
            {tabs.map(tab => <Tab key={tab.value} label={tab.label} value={tab.value} />)}
          </Tabs>
          <Divider className={classes.divider} />
          <div className={classes.content}>
            {tab === "summary" && <Summary handleClose={handleClose} handleDelete={handleDelete} reload={reload} />}
            {tab === "bookings" && <BookingList slot={model} />}
          </div>
        </RenderGuard>
      </Page>
    </AuthGuard>
  );
};

SlotDetail.propTypes = {
  history: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired
};

export default SlotDetail;
